/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";

interface BNSL129NoticePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  onSuccess?: () => void;
}

export function BNSL129NoticePopup({ open, onOpenChange, documentId, onSuccess }: BNSL129NoticePopupProps) {
  const [formData, setFormData] = useState({
    issuingOfficerName: "",
    issuingOfficerRank: "",
    reportDate: "",
    noticeIssueDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showError = useErrorStore((s) => s.showError);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    // noticeIssueDate is optional: court notice for 135(3) is usually never received
    if (!formData.issuingOfficerName || !formData.issuingOfficerRank || !formData.reportDate) {
      showError("Issuing officer name, rank and report sent date are required");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.post("bnsl129-notice", JSON.stringify({
        documentId,
        issuingOfficerName: formData.issuingOfficerName,
        issuingOfficerRank: formData.issuingOfficerRank,
        reportDate: new Date(formData.reportDate).toISOString(),
        noticeIssueDate: formData.noticeIssueDate ? new Date(formData.noticeIssueDate).toISOString() : null,
      }));

      if (response) {
        toast.success("BNSL 129 Notice created successfully!");
        onOpenChange(false);
        if (onSuccess) onSuccess();
        // Reset form
        setFormData({
          issuingOfficerName: "",
          issuingOfficerRank: "",
          reportDate: "",
          noticeIssueDate: "",
        });
      }
    } catch (error: any) {
      console.error("Error creating BNSL 129 Notice:", error);
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create BNSL 129 Notice (CrPC 116(3)/BNSS 135(3))</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="issuingOfficerName">Issuing Officer Name *</Label>
              <Input
                id="issuingOfficerName"
                value={formData.issuingOfficerName}
                onChange={(e) => handleInputChange("issuingOfficerName", e.target.value)}
                placeholder="Enter issuing officer name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="issuingOfficerRank">Issuing Officer Rank *</Label>
              <Input
                id="issuingOfficerRank"
                value={formData.issuingOfficerRank}
                onChange={(e) => handleInputChange("issuingOfficerRank", e.target.value)}
                placeholder="Enter officer rank"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reportDate">Report Sent Date / रिपोर्ट प्रेषण दिनांक *</Label>
              <Input
                id="reportDate"
                type="date"
                value={formData.reportDate}
                onChange={(e) => handleInputChange("reportDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="noticeIssueDate">Court Notice Date / न्यायालय नोटिस दिनांक (if received)</Label>
              <Input
                id="noticeIssueDate"
                type="date"
                value={formData.noticeIssueDate}
                onChange={(e) => handleInputChange("noticeIssueDate", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create BNSL 129 Notice"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
